import useThemeColors from '@/hooks/useThemeColors';
import { User } from '@/types';
import clsx from 'clsx';
import React from 'react';

interface AvatarProps {
    user: User;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
    showOnlineIndicator?: boolean;
}

const Avatar: React.FC<AvatarProps> = ({
    user,
    size = 'md',
    className = '',
    showOnlineIndicator = false,
}) => {
    const colors = useThemeColors();

    // Size mapping
    const sizeClasses = {
        sm: 'h-8 w-8 text-sm',
        md: 'h-10 w-10 text-base',
        lg: 'h-16 w-16 text-xl',
        xl: 'h-20 w-20 text-2xl',
    };

    // Online indicator size mapping
    const indicatorSizeClasses = {
        sm: 'h-2 w-2 -right-0.5 -bottom-0.5',
        md: 'h-3 w-3 -right-0.5 -bottom-0.5',
        lg: 'h-4 w-4 -right-1 -bottom-1',
        xl: 'h-6 w-6 -right-1 -bottom-1',
    };

    // Get user's initials
    const getInitials = () => {
        if (user.first_name) {
            const firstInitial = user.first_name[0]?.toUpperCase() || '';
            const lastInitial = user.last_name?.[0]?.toUpperCase() || '';
            return firstInitial + lastInitial;
        }

        if (user.email) {
            return user.email[0]?.toUpperCase() || 'U';
        }

        return 'U';
    };

    const avatarClasses = clsx(
        'flex items-center justify-center rounded-full font-semibold text-white object-cover',
        sizeClasses[size],
        !user.avatar_url && colors.primaryBg,
        className
    );

    return (
        <div className="relative inline-block">
            {user.avatar_url ? (
                <img
                    src={user.avatar_url || ''}
                    alt={user.full_name || user.email || 'User avatar'}
                    className={avatarClasses}
                />
            ) : (
                <div className={avatarClasses}>{getInitials()}</div>
            )}

            {showOnlineIndicator && (
                <div
                    className={clsx(
                        'absolute rounded-full border-2 border-white bg-green-500',
                        indicatorSizeClasses[size]
                    )}
                />
            )}
        </div>
    );
};

export default Avatar;
